/**
 * Headlight-FormLoadTiming
 *
 * Phase 0 instrumentation for the form load latency work (the plan lives at
 * `docs/plans/form-load-latency-assessment.md`, §5 Phase 0). Drops a
 * `performance.mark()` at each of the five points that bound a form open and
 * emits one summary log line when the last one lands.
 *
 * The phases, in the order they are expected to arrive:
 *
 *	 - `Boot`			- the HeadlightConfigurableApplication constructor.
 *						  Bounds script parse + eval before the app exists.
 *	 - `SessionReady`	- `onBeforeLoginAsync` .finally(). Bounds the cached
 *						  session read or the CheckSession round trip.
 *	 - `DocumentLoaded` - `onLoadDataAsync` .then(). Bounds the
 *						  ReportParameters fetch plus the full user record.
 *	 - `FirstRender`	- `onAfterLoadDataAsync`, once the form sections
 *						  have rendered. Bounds the InitialBundle fetch, the
 *						  manifest bootstrap and the section render.
 *	 - `SpinnerRemoved` - the application's `removeLoadingSpinner()`. Bounds
 *						  whatever still covers the form after it is drawn.
 *
 * A mark's `startTime` is milliseconds since `performance.timeOrigin` - that
 * is, since navigation started - so `Boot` already carries the cost of
 * everything that ran before the application was constructed, and
 * `SpinnerRemoved` is the click-to-drawn number Phase 0 targets (p50 under 4 s
 * on QA for a 50-descriptor form).
 *
 * `SpinnerRemoved` is marked when `removeLoadingSpinner()` is CALLED, not when
 * the 200 ms fade finishes. The form underneath is already drawn at that
 * point; the fade is cosmetic, and excluding it keeps the number comparable
 * across apps that fade differently.
 *
 * This changes no behavior. Every entry point is guarded so a missing or
 * throwing `performance` API - node tests, jsdom, the offline template - is a
 * no-op, and each phase is recorded only once, so a re-login or a re-render
 * cannot overwrite the first-open numbers.
 *
 * Three ways to read the results:
 *
 *	 - the log line, prefixed `[FormLoad]` so it greps next to the existing
 *	   `[populateCanEditField]` timing lines;
 *	 - `window._HeadlightFormLoad`, for a harness or a console snippet;
 *	 - in DevTools, the marks and the span between each adjacent pair:
 *	   `performance.getEntriesByType('mark').filter((e) => e.name.startsWith('HeadlightFormLoad:'))`
 */

/** Prefix on every mark and measure name, so the entries filter cleanly out of the Performance timeline. */
const MARK_PREFIX = 'HeadlightFormLoad:';

/** The phases, in expected arrival order. The summary renders them in this order regardless of when they land. */
const PHASES = [ 'Boot', 'SessionReady', 'DocumentLoaded', 'FirstRender', 'SpinnerRemoved' ];

/**
 * Records the five form-load phases and emits the one-line summary.
 */
class HeadlightFormLoadTiming
{
	/**
	 * @param {Object} [pOptions] - Options.
	 * @param {any} [pOptions.Log] - A pict/fable logger. Optional; without one the summary is only on `window`.
	 * @param {any} [pOptions.Performance] - The `performance` object to use. Defaults to the global one. Injectable for tests.
	 * @param {String} [pOptions.Context] - A label for the log line (the application name, typically).
	 * @param {String} [pOptions.TerminalPhase] - The phase whose arrival emits the summary. Defaults to the last phase.
	 */
	constructor(pOptions)
	{
		const tmpOptions = pOptions || {};
		this.log = tmpOptions.Log || null;
		this.performance = tmpOptions.Performance || ((typeof globalThis !== 'undefined') ? globalThis.performance : null);
		this.Context = tmpOptions.Context || 'Form';
		this.TerminalPhase = tmpOptions.TerminalPhase || PHASES[PHASES.length - 1];

		/**
		 * Milliseconds since navigation start, per phase, in arrival order.
		 * @type {Object<String, Number>}
		 */
		this.Phases = {};
		this.SummaryEmitted = false;
		this._warnedPhases = {};
	}

	/**
	 * Whether the environment can be measured at all.
	 *
	 * @return {Boolean} True when a usable `performance` object is present.
	 */
	get enabled()
	{
		return !!(this.performance && (typeof this.performance.now === 'function'));
	}

	/**
	 * Record a phase. The first call for a phase wins; later calls are ignored.
	 *
	 * @param {String} pPhase - One of the PHASES names.
	 * @return {Boolean} True when this call recorded the phase.
	 */
	mark(pPhase)
	{
		if (!this.enabled || !pPhase)
		{
			return false;
		}
		if (PHASES.indexOf(pPhase) < 0)
		{
			// A typo in a call site would otherwise vanish silently. Warn once per name.
			if (this.log && !this._warnedPhases[pPhase])
			{
				this._warnedPhases[pPhase] = true;
				this.log.warn(`[FormLoad] ignoring unknown phase "${pPhase}"`);
			}
			return false;
		}
		if (pPhase in this.Phases)
		{
			return false;
		}

		try
		{
			const tmpName = MARK_PREFIX + pPhase;
			const tmpMark = this.performance.mark(tmpName);
			// performance.mark() returns the entry in current browsers; older ones return undefined.
			this.Phases[pPhase] = (tmpMark && (typeof tmpMark.startTime === 'number')) ?
				tmpMark.startTime :
				this._lookupMarkStartTime(tmpName);
			this._measureFromPreviousPhase(pPhase);
		}
		catch (pError)
		{
			// Instrumentation must never be able to break a form load.
			return false;
		}

		if (pPhase === this.TerminalPhase)
		{
			this.emitSummary();
		}
		return true;
	}

	/**
	 * Resolve a mark's start time when `performance.mark()` did not hand one back.
	 *
	 * @param {String} pName - The full mark name.
	 * @return {Number} Milliseconds since navigation start.
	 */
	_lookupMarkStartTime(pName)
	{
		if (typeof this.performance.getEntriesByName === 'function')
		{
			const tmpEntries = this.performance.getEntriesByName(pName, 'mark');
			if (tmpEntries && tmpEntries.length)
			{
				return tmpEntries[tmpEntries.length - 1].startTime;
			}
		}
		return this.performance.now();
	}

	/**
	 * Draw a measure from the most recently recorded earlier phase, so DevTools shows the span.
	 *
	 * @param {String} pPhase - The phase just recorded.
	 */
	_measureFromPreviousPhase(pPhase)
	{
		if (typeof this.performance.measure !== 'function')
		{
			return;
		}
		const tmpRecorded = PHASES.filter((pName) => (pName in this.Phases));
		const tmpIndex = tmpRecorded.indexOf(pPhase);
		if (tmpIndex < 1)
		{
			return;
		}
		const tmpPrevious = tmpRecorded[tmpIndex - 1];
		try
		{
			this.performance.measure(`${MARK_PREFIX}${tmpPrevious}->${pPhase}`, MARK_PREFIX + tmpPrevious, MARK_PREFIX + pPhase);
		}
		catch (pError)
		{
			// A measure is a convenience; losing it is not worth a thrown error.
		}
	}

	/**
	 * The numbers behind the summary line.
	 *
	 * @return {{ Context: String, Phases: Object<String, Number>, Deltas: Object<String, Number>, TotalMS: Number, Complete: Boolean }}
	 */
	summary()
	{
		const tmpPhases = {};
		const tmpDeltas = {};
		let tmpPrevious = 0;
		let tmpLast = 0;
		for (const tmpPhase of PHASES)
		{
			if (!(tmpPhase in this.Phases))
			{
				continue;
			}
			const tmpAt = Math.round(this.Phases[tmpPhase]);
			tmpPhases[tmpPhase] = tmpAt;
			tmpDeltas[tmpPhase] = tmpAt - tmpPrevious;
			tmpPrevious = tmpAt;
			tmpLast = tmpAt;
		}
		return {
			Context: this.Context,
			Phases: tmpPhases,
			Deltas: tmpDeltas,
			TotalMS: tmpLast,
			Complete: PHASES.every((pPhase) => (pPhase in this.Phases)),
		};
	}

	/**
	 * Render the one-line summary.
	 *
	 * @return {String} The log line.
	 */
	formatSummary()
	{
		const tmpSummary = this.summary();
		const tmpParts = PHASES.map((pPhase) =>
		{
			if (!(pPhase in tmpSummary.Phases))
			{
				return `${pPhase} -`;
			}
			return `${pPhase} +${tmpSummary.Deltas[pPhase]}ms`;
		});
		return `[FormLoad] ${tmpSummary.Context} total ${tmpSummary.TotalMS}ms (navigation to drawn) - ${tmpParts.join(' | ')}`;
	}

	/**
	 * Publish the summary: one log line, plus the numbers on `window` for a harness to scrape.
	 * Runs once per page; later calls are ignored.
	 *
	 * @return {Boolean} True when this call emitted the summary.
	 */
	emitSummary()
	{
		if (this.SummaryEmitted || !this.enabled)
		{
			return false;
		}
		this.SummaryEmitted = true;
		const tmpSummary = this.summary();
		try
		{
			if (typeof window !== 'undefined')
			{
				window._HeadlightFormLoad = tmpSummary;
			}
		}
		catch (pError)
		{
			// No window (node, a worker) - the log line is still worth emitting.
		}
		try
		{
			if (this.log && (typeof this.log.info === 'function'))
			{
				this.log.info(this.formatSummary());
			}
		}
		catch (pError)
		{
			// SpinnerRemoved marks ahead of the spinner teardown, so a logger that throws here would
			// otherwise leave the form sitting behind the loading overlay.
			return false;
		}
		return true;
	}
}

module.exports = HeadlightFormLoadTiming;
module.exports.PHASES = PHASES;
module.exports.MARK_PREFIX = MARK_PREFIX;
